// features/workstations/tabs/think.js
// ═══════════════════════════════════════════════════════════
// THINK TAB — router: home (thread library) vs interior
// ═══════════════════════════════════════════════════════════
// Default landing = ThinkHome (2-per-row card grid of all
// threads in this workstation).
//
// Click a card or "+ New Thread" → interior view: the OLD
// Workstation 3-panel surface (chat + canvas + journey)
// scoped to the selected thread's shared_spaces row.
// "← Threads" back button in topbar slot.
//
// Each thread is its own shared_spaces row tagged with
// workstation_id (post-refactor, see think-thread.js).
// Workstation persona + rules + market state apply server-
// side via the chat router's workstation context lookup.
// Stance (observe / discover / neutral) is auto-detected
// per message — no UI toggles in V2.
// ═══════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';
import Workstation from '../../workstation';
import ThinkHome from '../think/think-home';

export default function ThinkTab({ workstation, userId, userRecord, supabase }) {
  // null = home, uuid = open this thread
  const [openThreadId, setOpenThreadId] = useState(null);
  const [threadSpace, setThreadSpace] = useState(null);
  const [loadingSpace, setLoadingSpace] = useState(false);
  const [error, setError] = useState(null);

  // When a thread is opened, fetch its shared_spaces row to pass
  // as workspaceSession to <Workstation>. Also touch last_opened_at
  // so it sorts to the top of the home grid.
  useEffect(() => {
    if (!openThreadId || !userId) {
      setThreadSpace(null);
      return;
    }
    let cancelled = false;
    setLoadingSpace(true);
    setError(null);
    (async () => {
      try {
        // Fetch the thread's shared_spaces row directly. We can use
        // the supabase service hop via the API: there's no "get
        // single thread" endpoint, but the thread list returns
        // metadata; for the workspaceSession we need the full row.
        // Easiest: hit Supabase via the user's session.
        const { data: { session: authSess } } = await supabase.auth.getSession();
        const headers = { 'Authorization': `Bearer ${authSess?.access_token || ''}` };
        // Direct supabase query (RLS will scope to user).
        const { data: row, error: rowErr } = await supabase
          .from('shared_spaces')
          .select('*')
          .eq('id', openThreadId)
          .maybeSingle();
        if (cancelled) return;
        if (rowErr || !row) {
          throw new Error(rowErr?.message || 'Thread not found');
        }
        setThreadSpace(row);
        // Fire-and-forget: touch last_opened_at
        fetch('/api/workstations/think-thread?action=open', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...headers },
          body: JSON.stringify({
            workstation_id: workstation.id,
            user_id: userId,
            thread_id: openThreadId,
          }),
        }).catch(() => {});
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to open thread');
      } finally {
        if (!cancelled) setLoadingSpace(false);
      }
    })();
    return () => { cancelled = true; };
  }, [openThreadId, userId, workstation?.id, supabase]);

  if (!workstation || !userId || !supabase) {
    return (
      <div className="ws-tab-content">
        <div className="ws-placeholder">
          <div className="ws-placeholder-eyebrow">Loading</div>
          <div className="ws-placeholder-title">Think</div>
          <div className="ws-placeholder-body">Preparing workstation context…</div>
        </div>
      </div>
    );
  }

  // ── HOME ──────────────────────────────────────────────
  if (!openThreadId) {
    return (
      <div className="ws-tab-content">
        <ThinkHome
          workstation={workstation}
          userId={userId}
          onOpenThread={(id) => setOpenThreadId(id)}
        />
      </div>
    );
  }

  // ── INTERIOR (loading thread) ─────────────────────────
  if (loadingSpace || !threadSpace) {
    return (
      <div className="ws-tab-content">
        <div className="ws-loading">{error ? error : 'Opening thread…'}</div>
      </div>
    );
  }

  // ── INTERIOR (thread open) ────────────────────────────
  // Topbar slot = "← Threads" back button. The Workstation's
  // own topbar still shows the SOLO • workstation name on the
  // left + file/layout buttons + Excavate on the right; we
  // inject a back affordance in between via topbarSlot.
  const topbarSlot = (
    <div style={{ display: 'flex', alignItems: 'center' }}>
      <button
        type="button"
        onClick={() => setOpenThreadId(null)}
        title="Back to threads"
        style={{
          fontFamily: "'DM Mono', monospace",
          fontSize: 9,
          letterSpacing: '0.10em',
          textTransform: 'uppercase',
          padding: '4px 10px',
          borderRadius: 4,
          border: '1px solid var(--ws-b2)',
          background: 'transparent',
          color: 'var(--ws-static)',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 5,
        }}
      >
        ← Threads
      </button>
    </div>
  );

  return (
    <div className="ws-tab-content ws-tab-think" style={{
      padding: 0,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    }}>
      <Workstation
        // Re-mount on thread change so message state hydrates fresh.
        key={openThreadId}
        userId={userId}
        supabase={supabase}
        userRecord={userRecord}
        workspaceSession={threadSpace}
        voiceConfig={null}
        onExit={() => setOpenThreadId(null)}
        onSidebarCollapse={() => { /* shell handles sidebar */ }}
        embedded={true}
        hideParticipants={true}
        skipOpener={true}
        topbarSlot={topbarSlot}
      />
    </div>
  );
}
